import { useState } from 'react'
import { api } from '../api/client'
import { connectGoogleGroups, googleGroupsToken, forgetGoogleGroupsToken } from '../auth'
import { useLoadable, ErrorBlock, EmptyBlock, Pill } from '../components/ui'
import type { GroupChange, GroupChangeStatus, GroupMember, MyGroups, OwnedGroup } from '../types'

/* My groups — owners add and remove the people in the access groups they own.
   Every read and change runs in Google on the owner's own account (Cloud Identity
   Groups API), so the portal can do nothing here that the owner could not do in
   groups.google.com. What it adds: each group's full reach shown before anyone is
   added, and a record of who changed what and why. Owners can add members and owners
   (or promote a member); removing an owner or manager stays with the RMS team. */

type Err = Error & { code?: string; correlationId?: string }

const STATUS_LABEL: Record<GroupChangeStatus, string> = {
  applying: 'In progress',
  confirmed: 'Done in Google',
  already: 'Nothing to change',
  failed: 'Not changed',
  unknown: 'Not confirmed yet',
}

const RADIO = { width: 18, height: 18, margin: 0, flex: 'none', accentColor: 'var(--accent)' } as const

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

function StatusPill({ status }: { status: GroupChangeStatus }) {
  const tone = status === 'failed' ? 'red' : status === 'unknown' ? 'amber' : 'neutral'
  return <Pill tone={tone}>{STATUS_LABEL[status]}</Pill>
}

function ChangeLine({ c }: { c: GroupChange }) {
  const verb = c.action === 'add' ? 'Add' : 'Remove'
  return (
    <p role="status" style={{ marginBottom: 0 }}>
      <StatusPill status={c.status} /> {verb} <b>{c.memberEmail}</b>{c.action === 'add' && c.role === 'OWNER' && ' as owner'}
      {c.status === 'already' && (c.action === 'add' ? ': already in the group.' : ': was not in the group.')}
      {c.status === 'unknown' && ': Google has not confirmed it yet. Refresh the member list before trying again.'}
      {c.error && <><br /><span className="note">{c.error}</span></>}
    </p>
  )
}

/** Reach first: adding someone gives them every folder below, not just one. */
function Reach({ group }: { group: OwnedGroup }) {
  const limited = group.folders.filter((f) => f.limitedAccess).length
  return (
    <>
      <p style={{ marginBottom: 'var(--s-xs)' }}>
        Anyone in this group can open <b>{group.folders.length} folder{group.folders.length === 1 ? '' : 's'}</b>
        {limited > 0 && <>, including {limited} with limited access</>}:
      </p>
      <ul style={{ marginTop: 0, paddingLeft: '1.2rem' }}>
        {group.folders.map((f) => (
          <li key={f.folderId}>
            {f.name}{!f.name.endsWith(` · ${f.driveName}`) && <span className="note"> · {f.driveName}</span>}
            {f.limitedAccess && <> <Pill>Limited access</Pill></>}
          </li>
        ))}
      </ul>
    </>
  )
}

function Members({ group, changesEnabled, me, onNeedGoogle }: {
  group: OwnedGroup; changesEnabled: boolean; me: string; onNeedGoogle: () => void
}) {
  const [members, setMembers] = useState<GroupMember[] | null>(null)
  const [loadError, setLoadError] = useState<Err | null>(null)
  const [email, setEmail] = useState('')
  const [reason, setReason] = useState('')
  const [role, setRole] = useState<'MEMBER' | 'OWNER'>('MEMBER')
  const [busy, setBusy] = useState(false)
  const [last, setLast] = useState<GroupChange | null>(null)
  const [changeError, setChangeError] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const [history, setHistory] = useState<GroupChange[] | null>(null)
  /* One key per intended change, so a double click or a retry is applied once. */
  const [keys] = useState(() => new Map<string, string>())
  const keyFor = (k: string) => { let v = keys.get(k); if (!v) { v = crypto.randomUUID(); keys.set(k, v) } return v }

  const handle = (err: unknown) => {
    const e = err as Err
    if (e.code === 'google_token') { forgetGoogleGroupsToken(); onNeedGoogle() }
    return e
  }

  const load = async () => {
    setLoadError(null)
    try {
      setMembers((await api.getGroupMembers(group.email)).members)
    } catch (err) {
      setLoadError(handle(err))
    }
  }

  const change = async (action: 'add' | 'remove', target: string, why: string, asRole: 'MEMBER' | 'OWNER' = 'MEMBER') => {
    setBusy(true); setChangeError(null); setLast(null)
    const k = `${action}|${asRole}|${target}|${why}`
    try {
      const { change: c } = await api.changeGroupMember(group.email, action, target, why, keyFor(k), asRole)
      setLast(c)
      if (c.status === 'confirmed' || c.status === 'already') {
        keys.delete(k)
        if (action === 'add') { setEmail(''); setReason(''); setRole('MEMBER') }
        setRemoving(null)
      }
      await load()
      if (history) setHistory((await api.getGroupChanges(group.email)).changes)
    } catch (err) {
      setChangeError(handle(err).message)
    } finally {
      setBusy(false)
    }
  }

  if (!members) {
    return (
      <div style={{ display: 'flex', gap: 'var(--s-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn--secondary" onClick={load}>Show members</button>
        {loadError && <span role="alert"><Pill tone={loadError.code === 'google_token' ? 'amber' : 'red'}>Could not load</Pill> {loadError.message}</span>}
      </div>
    )
  }

  const canChange = changesEnabled
  return (
    <>
      <div className="tablewrap">
        <table>
          <thead><tr><th>Person</th><th>Role</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.email}>
                <td className="mono">{m.email}{m.type !== 'USER' && <span className="note"> · {m.type.toLowerCase().replace('_', ' ')}</span>}</td>
                <td>{m.role === 'MEMBER' ? 'Member' : m.role === 'OWNER' ? 'Owner' : 'Manager'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canChange && m.role === 'MEMBER' && m.email !== me && removing !== m.email && (
                    <button className="btn btn--ghost" disabled={busy} onClick={() => setRemoving(m.email)}>Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="note">{members.length} {members.length === 1 ? 'person' : 'people'}. You can add owners; removing an owner or manager is done by the RMS team.</p>

      {removing && (
        <RemoveForm email={removing} busy={busy} onCancel={() => setRemoving(null)}
                    onConfirm={(why) => change('remove', removing, why)} />
      )}

      {canChange && !removing && (
        <form style={{ display: 'grid', gap: 'var(--s-sm)', maxWidth: 520, marginTop: 'var(--s-md)' }}
              onSubmit={(e) => { e.preventDefault(); change('add', email.trim().toLowerCase(), reason.trim(), role) }}>
          <h3 style={{ margin: 0 }}>Add someone</h3>
          <label htmlFor={`add-${group.email}`}>Their d.light email</label>
          <input className="field" id={`add-${group.email}`} type="email" value={email} autoComplete="off"
                 onChange={(e) => setEmail(e.target.value)} placeholder="jane.doe@dlight.com" />
          <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--s-xs)' }}>
            <legend style={{ marginBottom: 'var(--s-xs)' }}>As</legend>
            <label style={{ display: 'flex', gap: 'var(--s-sm)', alignItems: 'center', minHeight: 44 }}>
              <input type="radio" style={RADIO} name={`role-${group.email}`} checked={role === 'MEMBER'} onChange={() => setRole('MEMBER')} />
              Member: can open the folders above
            </label>
            <label style={{ display: 'flex', gap: 'var(--s-sm)', alignItems: 'center', minHeight: 44 }}>
              <input type="radio" style={RADIO} name={`role-${group.email}`} checked={role === 'OWNER'} onChange={() => setRole('OWNER')} />
              Owner: can open the folders and add or remove people in this group
            </label>
            {role === 'OWNER' && (
              <span className="note">Already a member? They become an owner. Owners are removed by the RMS team.</span>
            )}
          </fieldset>
          <label htmlFor={`why-${group.email}`}>Why (kept with the change)</label>
          <input className="field" id={`why-${group.email}`} value={reason}
                 onChange={(e) => setReason(e.target.value)} placeholder="e.g. Joined KE Sales Operations" />
          <div>
            <button className="btn btn--primary" type="submit" disabled={busy || !email.includes('@') || reason.trim().length < 5}>
              {busy ? 'Adding…' : role === 'OWNER' ? 'Add as owner' : `Add to ${group.folders.length} folder${group.folders.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </form>
      )}

      {last && <ChangeLine c={last} />}
      {changeError && <p role="alert"><Pill tone="red">Not changed</Pill> {changeError}</p>}

      <div style={{ marginTop: 'var(--s-md)' }}>
        {history === null ? (
          <button className="btn btn--ghost" onClick={async () => setHistory((await api.getGroupChanges(group.email)).changes)}>
            Changes made in RMS →
          </button>
        ) : history.length === 0 ? (
          <p className="note">No changes made through RMS yet. Changes made in Google Groups are not listed here.</p>
        ) : (
          <div className="tablewrap">
            <table>
              <thead><tr><th>When</th><th>Change</th><th>By</th><th>Why</th><th>Result</th></tr></thead>
              <tbody>
                {history.map((c) => (
                  <tr key={c.changeId}>
                    <td style={{ whiteSpace: 'nowrap' }}>{when(c.createdAt)}</td>
                    <td>{c.action === 'add' ? 'Added' : 'Removed'} <span className="mono">{c.memberEmail}</span>{c.action === 'add' && c.role === 'OWNER' && ' as owner'}</td>
                    <td className="mono">{c.actorEmail}</td>
                    <td>{c.reason}</td>
                    <td><StatusPill status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

function RemoveForm({ email, busy, onCancel, onConfirm }: {
  email: string; busy: boolean; onCancel: () => void; onConfirm: (why: string) => void
}) {
  const [why, setWhy] = useState('')
  return (
    <div className="block block--sunk" style={{ display: 'grid', gap: 'var(--s-sm)', maxWidth: 520 }}>
      <p style={{ margin: 0 }}>Remove <b>{email}</b>? They lose every folder this group opens, unless another group gives them access.</p>
      <label htmlFor={`rm-${email}`}>Why (kept with the change)</label>
      <input className="field" id={`rm-${email}`} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="e.g. Moved to Finance" />
      <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap' }}>
        <button className="btn btn--primary" disabled={busy || why.trim().length < 5} onClick={() => onConfirm(why.trim())}>
          {busy ? 'Removing…' : 'Remove from group'}
        </button>
        <button className="btn btn--ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

export default function GroupsRoute({ me }: { me: string }) {
  const [data, retry] = useLoadable<MyGroups>(() => api.getMyGroups(), (d) => d.groups.length === 0, [])
  const [connected, setConnected] = useState(() => !!googleGroupsToken())
  const [connectError, setConnectError] = useState<string | null>(null)

  const connect = async () => {
    setConnectError(null)
    try {
      await connectGoogleGroups()
      setConnected(!!googleGroupsToken())
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Google sign-in was cancelled.')
    }
  }

  if (data.status === 'error') return <ErrorBlock message={data.message} correlationId={data.correlationId} onRetry={retry} />

  return (
    <>
      <p className="eyebrow">My groups</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>
        {data.status === 'ready' ? `${data.data.groups.length} access group${data.data.groups.length === 1 ? '' : 's'} you own` : 'Access groups you own'}
      </h1>
      <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
        Folder access comes from groups. Adding someone to a group gives them every folder listed under it,
        so check the list first. Changes are made in Google with your own account, just as in Google Groups,
        and RMS keeps a record of who changed what and why. People on Frontline licences can view but not
        edit, whatever the group allows. The list of groups you own is updated each night.
      </p>

      {data.status === 'ready' && !data.data.changesEnabled && (
        <p className="note"><Pill>View only</Pill> Adding and removing people here is not switched on yet. Use Google Groups meanwhile.</p>
      )}

      {data.status === 'ready' && !connected && (
        <div className="block" style={{ borderLeft: '3px solid var(--accent)', marginBottom: 'var(--s-md)' }}>
          <h3 style={{ marginTop: 0 }}>Connect Google to see and change members</h3>
          <p>Google will ask you to allow RMS to manage your groups. It acts only as you, on groups you own, and stays connected for up to an hour in this tab.</p>
          <button className="btn btn--primary" onClick={connect}>Connect Google</button>
          {connectError && <p role="alert"><Pill tone="red">Not connected</Pill> {connectError}</p>}
        </div>
      )}

      {data.status === 'loading' && (
        <div aria-busy="true">{[0, 1].map((i) => <div className="skeleton" key={i} style={{ height: '8rem', marginBottom: 12 }} />)}</div>
      )}

      {data.status === 'empty' && (
        <EmptyBlock title="You do not own an access group">
          Group owners are set by the RMS team. If you look after a department's folders and should
          add people yourself, ask the RMS team. Groups given to you today show here from tomorrow.
        </EmptyBlock>
      )}

      {data.status === 'ready' && data.data.groups.map((g) => (
        <section className="block" key={g.email} style={{ marginBottom: 'var(--s-md)' }}>
          <h2 className="mono" style={{ fontSize: '1rem', marginTop: 0, wordBreak: 'break-all' }}>{g.email}</h2>
          <Reach group={g} />
          {connected
            ? <Members group={g} changesEnabled={data.data.changesEnabled} me={me.toLowerCase()} onNeedGoogle={() => setConnected(false)} />
            : <p className="note">Connect Google above to see who is in this group.</p>}
        </section>
      ))}
    </>
  )
}
